import type { Me } from "./api.js";
import { isOwner } from "./auth.js";
import { paymentWorkflowAccess } from "./booking/paymentWorkflow.js";

export type StaffQuickActionId = "book-lesson" | "log-payment" | "add-student" | "block-time" | "recurring-lesson";

export interface StaffQuickAction {
  id: StaffQuickActionId;
  label: string;
  to: string;
}

/**
 * The shell only offers quick entry points the signed-in account can
 * actually reach. This mirrors the existing workflow seams rather than
 * inventing a second permission model:
 *
 * - ordinary booking/recurrence creation is pinned to the caller's linked
 *   provider by the canonical forms; owners use the forms' existing on-behalf
 *   path while the owner-all compatibility policy remains in force;
 * - blocked-time management additionally has an explicit all-provider path via
 *   `schedules.manage.all`, which the canonical Time off screen already uses.
 *   It still needs `booking.staff`: schedule blocks are read/written through
 *   `/api/staff-api`, and that transport is mounted only when the staff booking
 *   integration is connected. The always-visible Time off navigation is a
 *   broader shell inconsistency, not authority the quick launcher should copy;
 * - adding a Hub student is intentionally separate from `customers.manage`:
 *   ordinary tutors already have a server-scoped private-student creation flow;
 * - payment logging uses the canonical `paymentWorkflowAccess` check for
 *   individual lessons and lesson blocks; the existing payment page handles both.
 */
export function staffQuickActions(me: Me): StaffQuickAction[] {
  const held = new Set(me.capabilities);
  const owner = isOwner(me);
  const bookingConnected = me.booking.staff === true;
  const canUseBookingCreator = me.linkedToProvider || owner;
  const canManageSchedule = me.linkedToProvider || owner || held.has("schedules.manage.all");
  const canAddStudent = owner || me.roles.includes("tutor");
  const paymentAccess = paymentWorkflowAccess(me);

  return [
    ...(bookingConnected && canUseBookingCreator
      ? [
          {
            id: "book-lesson" as const,
            label: "Book lesson",
            to: "/calendar/book",
          },
        ]
      : []),
    ...(paymentAccess.lessons || paymentAccess.blocks
      ? [{ id: "log-payment" as const, label: "Log payment", to: "/payments/log" }]
      : []),
    ...(canAddStudent
      ? [
          {
            id: "add-student" as const,
            label: "Add student",
            // Owners use the whole-school creation form; tutors reuse their
            // existing private-student form in the scoped roster.
            to: owner ? "/students?new=1" : "/students?scope=mine&new=1",
          },
        ]
      : []),
    ...(bookingConnected && canManageSchedule
      ? [
          {
            id: "block-time" as const,
            label: "Block time",
            to: "/time-off",
          },
        ]
      : []),
    ...(bookingConnected && canUseBookingCreator
      ? [
          {
            id: "recurring-lesson" as const,
            label: "Recurring lesson",
            to: "/recurring-lessons",
          },
        ]
      : []),
  ];
}
